import {
    Component,
    Input,
    ViewEncapsulation,
    ChangeDetectorRef,
    SimpleChanges,
    OnChanges
} from '@angular/core';
import { ProductInterface } from 'src/app/model/products/product-interface';
import { MatFormFieldControl } from '@angular/material/form-field';
import { VariantVersionInterface } from 'src/app/model/products/variant-version-interface';
import { PtgVersionInterface } from 'src/app/model/ptgs/ptg-version-interface';
import { CommonFeatureService } from 'src/app/services/common-feature-service/common-feature.service';
import { ProductCommonFeatureInterface } from 'src/app/model/commonFeature/product-common-feature-interface';
import { CertificationInterface } from 'src/app/model/certifications/certification-interface';
import {
    ProductTypeGroupFeatureDisplayInterface,
    ProductTypeGroupFeaturesDisplayInterface
} from 'src/app/model/marketplace/product-type-group-features-interface';
import { PtgAttributeValidationInterface } from 'src/app/model/attributes/ptg-attribute-validation-interface';
import { AttributeInterface } from 'src/app/model/attributes/attribute-interface';
@Component({
    selector: 'app-product-detail-display-feature',
    templateUrl: './product-detail-display-feature.component.html',
    styleUrls: [
        './product-detail-display-feature.component.css',
        '../product-detail-display.css'
    ],
    providers: [
        {
            provide: MatFormFieldControl,
            useExisting: ProductDetailDisplayFeatureComponent
        }
    ],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ProductDetailDisplayFeatureComponent implements OnChanges {
    @Input()
    product: ProductInterface;

    @Input()
    certification: CertificationInterface;

    @Input()
    ptg_names: string[] = [];
    version: VariantVersionInterface;
    @Input() licenseModels: string[];
    productFeatures: string[];
    displayedColumnsForCommonFeatures: string[] = ['feature', 'value'];

    public productFeatureToggle: boolean = false;
    public ptgToggle: boolean = false;
    public commonFeaturesToggle: boolean = false;
    public attributeToggle: boolean = false;
    productCommonFeatures: ProductCommonFeatureInterface[];
    ptgVersionFeatures: ProductTypeGroupFeaturesDisplayInterface[];

    constructor(
        private commonFeatureService: CommonFeatureService,
        private cd: ChangeDetectorRef
    ) { }

    ngOnChanges(products: SimpleChanges) {
        if (products != null) {
            this.product = products.product.currentValue;
        }
        if (this.product.productVariants != null) {
            this.product.productVariants.map((x) => {
                this.version = x;
            });

            this.productFeatures = this.product.features;
            this.commonFeatureService
                .getFeaturesForProduct(this.product.id)
                .subscribe((success: ProductCommonFeatureInterface[]) => {
                    this.productCommonFeatures = success;
                    this.commonFeaturesToggle = success.length > 0;
                });
        }
    }

    public onVersionSelected(version: VariantVersionInterface): void {
        this.version = version;
        this.updatePTGlist(version);
        this.attributeToggle = version.ptgVersions.some(
            (ptgVersion) =>
                ptgVersion.attributeValidations != null &&
                ptgVersion.attributeValidations.length > 0
        );
        this.productFeatureToggle =
            this.productFeatures.length > 0 ? true : false;
        this.ptgToggle = this.ptgVersionFeatures.length > 0 ? true : false;
        this.cd.detectChanges();
    }

    private updatePTGlist(version: VariantVersionInterface): void {
        this.ptgVersionFeatures = version.ptgVersions
            .sort((a, b) =>
                a.ptgName.toLowerCase().localeCompare(b.ptgName.toLowerCase())
            )
            .map((ptgVersion) => this.getPTGFeatures(ptgVersion));
        this.version = version;
        this.ptg_names = version.productTypeNames;
    }

    private getPTGFeatures(
        ptgVersion: PtgVersionInterface
    ): ProductTypeGroupFeaturesDisplayInterface {
        const ptgVersionFeatures: ProductTypeGroupFeaturesDisplayInterface = {
            ptgName: ptgVersion.ptgName,
            ptgDescription: this.removeHTMLTags(ptgVersion.ptgDescription),
            mandatoryFeatures: [],
            optionalFeatures: [],
            doesProjectFulfillAllFeaturesOfPTGVersion: true
        };
        for (const attributeVal of ptgVersion.attributeValidations) {
            const feature: ProductTypeGroupFeatureDisplayInterface = {
                featureName: attributeVal.attribute.name,
                featureDescription: attributeVal.attribute.description,
                featureValidation: this.getValidationValue(attributeVal),
                datatype: attributeVal.attribute.datatype
            };
            if (attributeVal.optionalValidation) {
                ptgVersionFeatures.optionalFeatures.push(feature);
            } else {
                ptgVersionFeatures.mandatoryFeatures.push(feature);
                if (
                    !this.isAttributeValueFulfillingValidation(
                        attributeVal.attribute,
                        feature.featureValidation + ''
                    )
                ) {
                    ptgVersionFeatures.doesProjectFulfillAllFeaturesOfPTGVersion =
                        false;
                }
            }
        }
        if (ptgVersionFeatures.mandatoryFeatures.length === 0) {
            ptgVersionFeatures.doesProjectFulfillAllFeaturesOfPTGVersion =
                false;
        }
        ptgVersionFeatures.mandatoryFeatures.sort((a, b) =>
            a.featureName
                .toLowerCase()
                .localeCompare(b.featureName.toLowerCase())
        );
        ptgVersionFeatures.optionalFeatures.sort((a, b) =>
            a.featureName
                .toLowerCase()
                .localeCompare(b.featureName.toLowerCase())
        );
        return ptgVersionFeatures;
    }

    private removeHTMLTags(text: string): string {
        return text.replace(/<[^>]*>/g, '');
    }

    private getValidationValue(
        attribute: PtgAttributeValidationInterface
    ): string {
        const validation = this.version.attributeValidationValues.find(
            (validationValue) =>
                validationValue.attribute.id === attribute.attribute.id
        );
        const validationValue = validation == null ? '' : validation.value;
        if (
            attribute.attribute.datatype === 'LIST' &&
            validationValue != null
        ) {
            return validationValue.replace(/;/g, ', ');
        }
        return validationValue;
    }

    private isAttributeValueFulfillingValidation(
        attribute: AttributeInterface,
        value: string
    ) {
        const validation = attribute.validations[0];
        switch (attribute.datatype) {
            case 'NUMERIC':
                return this.isNumberInRange(
                    Number(value),
                    validation.minSize,
                    validation.maxSize
                );
            case 'BOOLEAN':
                return (
                    attribute.validations[0].value == null ||
                    value == null ||
                    attribute.validations[0].value + '' == value + ''
                );
            case 'LIST':
                return this.isListValid(
                    validation.values,
                    validation.minSize,
                    validation.maxSize,
                    value.split(', ')
                );
            case 'STRING':
                return this.isNumberInRange(
                    value.length,
                    validation.minSize,
                    validation.maxSize
                );
        }
    }

    private isListValid(
        validationValues: string[],
        minCount: number,
        maxCount: number,
        selectedValues: string[]
    ): boolean {
        for (const selectedValue of selectedValues) {
            if (!validationValues.includes(selectedValue)) {
                return false;
            }
        }
        return this.isNumberInRange(selectedValues.length, minCount, maxCount);
    }

    private isNumberInRange(value: number, min: number, max: number): boolean {
        return (min == null || min <= value) && (max == null || max >= value);
    }
}
